import type { RecordAny } from './types';

export function splitPath(path: string): string[] {
  const normalized = path.trim();
  if (!normalized) return [];
  if (normalized.startsWith('/')) return normalized.replace(/^\/+/, '').split('/').filter(Boolean);
  return normalized.split('.').filter(Boolean);
}

export function walkPath(path: string, data: RecordAny): unknown {
  const parts = splitPath(path);
  let current: unknown = data;
  for (const part of parts) {
    if (!current || typeof current !== 'object') return undefined;
    current = (current as RecordAny)[part];
  }
  return current;
}

function hasOwn(record: RecordAny, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function resolveLiteral(record: RecordAny): unknown {
  if (hasOwn(record, 'literalString')) return record.literalString;
  if (hasOwn(record, 'literalNumber')) return record.literalNumber;
  if (hasOwn(record, 'literalBool')) return record.literalBool;
  if (hasOwn(record, 'literalBoolean')) return record.literalBoolean;
  if (hasOwn(record, 'literalArray')) return record.literalArray;
  if (hasOwn(record, 'literalObject')) return record.literalObject;
  return undefined;
}

function resolvePathOrLiteral(record: RecordAny, data: RecordAny): unknown {
  if (typeof record.path === 'string') {
    const current = walkPath(record.path, data);
    if (current !== undefined) return current;
  }
  return resolveLiteral(record);
}

function isPlainObject(value: unknown): value is RecordAny {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

export function resolvePath(expr: unknown): string | undefined {
  if (!expr || typeof expr !== 'object' || typeof (expr as RecordAny).path !== 'string') return undefined;
  const path = ((expr as RecordAny).path as string).trim();
  return path || undefined;
}

export function resolveLiteralBoolean(expr: unknown): boolean | undefined {
  if (!expr || typeof expr !== 'object') return undefined;
  const record = expr as RecordAny;
  if (typeof record.literalBool === 'boolean') return record.literalBool;
  if (typeof record.literalBoolean === 'boolean') return record.literalBoolean;
  return undefined;
}

export function valuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) && Array.isArray(right)) {
    if (left.length !== right.length) return false;
    return left.every((value, index) => valuesEqual(value, right[index]));
  }
  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every((key) => valuesEqual(left[key], right[key]));
  }
  return false;
}

export function setPathValue(data: RecordAny, path: string, value: unknown): RecordAny {
  const parts = splitPath(path);
  if (parts.length === 0) return data;
  const root: RecordAny = { ...data };
  let cursor: RecordAny = root;
  let source: unknown = data;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i]!;
    const nextSource = source && typeof source === 'object' ? (source as RecordAny)[key] : undefined;
    const next = isPlainObject(nextSource) ? { ...nextSource } : {};
    cursor[key] = next;
    cursor = next;
    source = nextSource;
  }
  cursor[parts[parts.length - 1]!] = value;
  return root;
}

export function resolveUnknown(expr: unknown, data: RecordAny): unknown {
  if (!expr || typeof expr !== 'object') return undefined;
  return resolvePathOrLiteral(expr as RecordAny, data);
}

export function resolveText(expr: unknown, data: RecordAny): string {
  if (!expr || typeof expr !== 'object') return '';
  const current = resolvePathOrLiteral(expr as RecordAny, data);
  if (typeof current === 'string' || typeof current === 'number') return String(current);
  return '';
}

export function resolveNumber(expr: unknown, data: RecordAny): number {
  if (!expr || typeof expr !== 'object') return 0;
  const current = resolvePathOrLiteral(expr as RecordAny, data);
  if (typeof current === 'number') return current;
  return 0;
}

export function resolveBool(expr: unknown, data: RecordAny): boolean {
  if (!expr || typeof expr !== 'object') return true;
  const current = resolvePathOrLiteral(expr as RecordAny, data);
  if (typeof current === 'boolean') return current;
  return true;
}

export function resolveLiteralStringArray(tags: unknown): string[] {
  if (!tags || typeof tags !== 'object') return [];
  const literal = (tags as RecordAny).literalArray;
  if (!Array.isArray(literal)) return [];
  return literal.filter((t): t is string => typeof t === 'string' && t.trim().length > 0);
}

export function resolveStringArray(expr: unknown, data: RecordAny): string[] {
  if (Array.isArray(expr)) return expr.filter((t): t is string => typeof t === 'string' && t.trim().length > 0);
  if (!expr || typeof expr !== 'object') return [];
  const record = expr as RecordAny;
  if (typeof record.path === 'string') {
    const current = walkPath(record.path, data);
    if (Array.isArray(current)) return current.filter((t): t is string => typeof t === 'string' && t.trim().length > 0);
  }
  if (Array.isArray(record.literalArray)) {
    return record.literalArray.filter((t): t is string => typeof t === 'string' && t.trim().length > 0);
  }
  return [];
}

export function resolveObjectArray(expr: unknown, data: RecordAny): RecordAny[] {
  if (Array.isArray(expr)) return expr.filter((it): it is RecordAny => Boolean(it && typeof it === 'object'));
  if (!expr || typeof expr !== 'object') return [];
  const record = expr as RecordAny;
  if (typeof record.path === 'string') {
    const current = walkPath(record.path, data);
    if (Array.isArray(current)) return current.filter((it): it is RecordAny => Boolean(it && typeof it === 'object'));
  }
  if (Array.isArray(record.literalArray)) {
    return record.literalArray.filter((it): it is RecordAny => Boolean(it && typeof it === 'object'));
  }
  return [];
}

export function resolveSegmentArray(segments: unknown, data: RecordAny): { text: string; style: string }[] {
  if (!segments || typeof segments !== 'object') return [];
  const record = segments as RecordAny;
  const pathValue = typeof record.path === 'string' ? walkPath(record.path, data) : undefined;
  const current = Array.isArray(pathValue) ? pathValue : record.literalArray;
  if (!Array.isArray(current)) return [];
  const out: { text: string; style: string }[] = [];
  for (const entry of current) {
    if (!entry || typeof entry !== 'object') continue;
    const text = (entry as RecordAny).text;
    if (typeof text !== 'string' || !text.trim()) continue;
    const styleRaw = (entry as RecordAny).style;
    const style: string = typeof styleRaw === 'string' ? styleRaw : 'default';
    out.push({ text, style });
  }
  return out;
}
